import { useRef, useState } from 'react';

import { UseQueryResult } from '@tanstack/react-query';

/**
 * @summary Polls a React Query resource until a selected value changes, then invokes a callback.
 *
 * @remarks
 * When {@link pollQuery} is called, the hook snapshots the current query data as a baseline, performs
 * an immediate refetch, and then continues polling at a fixed interval until either the selected value
 * changes or the configured number of attempts is exhausted.
 *
 * This is useful when a mutation starts a backend process that eventually flips a specific field
 * (for example, a feature access flag) and you want to defer global invalidations until the change
 * is visible from the backend.
 */

/**
 * @summary Configuration for {@link usePollingUpdater}.
 */
type UsePollingUpdater<
  TQuery extends UseQueryResult<unknown, unknown>,
  TArgs extends Record<string, unknown>,
  TValue,
> = {
  /** The React Query hook to read and refetch the data being polled. */
  useQuery: (arg0: TArgs) => TQuery;
  /** Arguments forwarded into the provided `useQuery` hook. */
  queryArgs?: TArgs;
  /**
   * Picks the specific value from the query data to compare between polls.
   * Defaults to identity (the entire data object).
   */
  selector?: (arg0: TQuery['data']) => TValue | undefined;
  /** Maximum number of additional poll attempts after the first refetch. Default: 8. */
  intervalCount?: number;
  /** Invoked once when the selected value changes versus the baseline snapshot. */
  onDataUpdated?: () => void;
};

/**
 * @summary Returns a function that starts a polling session and a flag indicating if polling is active.

 * @returns An object with:
 * - `pollQuery`: starts a polling session against the underlying query.
 * - `isPolling`: boolean flag set to `true` while a session is active.
 *
 * @example
 * ```ts
 * const { pollQuery, isPolling } = usePollingUpdater({
 *   useQuery: useShowConsumerBacker,
 *   selector: (d) => Boolean((d as ConsumerBackerResponse)?.backer?.student_fees_access),
 *   onDataUpdated: () => queryClient.invalidateQueries({ queryKey: backerKeys.consumer.backer.all }),
 * });
 *
 * // After a mutation settles, begin polling for the change to appear
 * onSettled: () => { void pollQuery(); };
 * ```
 */
export default function usePollingUpdater<
  TQuery extends UseQueryResult<unknown, unknown>,
  TArgs extends Record<string, unknown>,
  TValue,
>({
  useQuery,
  queryArgs = {} as TArgs,
  selector = (data) => data as unknown as TValue | undefined,
  intervalCount = 8,
  onDataUpdated,
}: UsePollingUpdater<TQuery, TArgs, TValue>) {
  const intervalMs = 2500;
  const { data, refetch } = useQuery(queryArgs);
  const [isPolling, setIsPolling] = useState(false);

  // Initial data snapshot used for equality checks during a polling session.
  // Always reset at poll start via the provided initialData or current data.
  const initialDataRef = useRef<TQuery['data'] | null>(null);

  /** Returns `true` when the selected value is unchanged between the two snapshots. */
  const isDataEqual = (oldData: TQuery['data'] | null, newData: TQuery['data'] | undefined) => {
    return selector(oldData as TQuery['data']) === selector(newData as TQuery['data']);
  };

  /** Recursive interval poller; continues until change detected or attempts exhausted. */
  const interval = async (currentInterval = 0) => {
    const { data: newData, error } = await refetch();

    // Exit poller on errors
    if (error) {
      setIsPolling(false);
      return;
    }

    // Exit once data changes vs the baseline snapshot
    if (isDataEqual(initialDataRef.current, newData) == false) {
      onDataUpdated?.();
      setIsPolling(false);
      return;
    }

    if (currentInterval <= 0) {
      setIsPolling(false);
      return;
    }

    setTimeout(() => {
      void interval(--currentInterval);
    }, intervalMs);
  };

  /**
   * @summary Starts a new polling session.
   * 1. Snapshot the provided `initialData` (or current data) as the initialData.
   * 2. Perform an immediate refetch and, if still equal, continue polling.
   *
   * @param initialData - Optional initialData data to compare against. If omitted, uses current query data.
   */
  const pollQuery = async ({ initialData }: { initialData?: TQuery['data'] } = {}) => {
    setIsPolling(true);
    initialDataRef.current = initialData ?? data ?? null;

    const { data: newData } = await refetch();

    if (isDataEqual(initialDataRef.current, newData)) {
      // Account for previous poll
      setTimeout(() => {
        void interval(intervalCount - 1);
      }, intervalMs);
    } else {
      // Already changed on first fetch; trigger update immediately
      onDataUpdated?.();
      setIsPolling(false);
    }
  };

  /** Starts polling and a flag indicating whether a polling session is active. */
  return { pollQuery, isPolling };
}
